import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

// CCAvenue order_status values: Success | Failure | Aborted | Initiated
export function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status") || "";
  const orderId = searchParams.get("orderId") || "";
  const [countdown, setCountdown] = useState(15);
  const [homeUrl, setHomeUrl] = useState("https://pcte.edu.in/");

  const isSuccess = status.toLowerCase() === "success";
  const isAborted = status.toLowerCase() === "aborted";

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("pcte")) {
      setHomeUrl("https://pcte.edu.in/");
    }
  }, []);

  useEffect(() => {
    document.title = isSuccess ? "Payment Successful" : "Payment Failed";
  }, [isSuccess]);

  useEffect(() => {
    if (countdown === 0) {
      window.location.href = homeUrl;
      return;
    }
    const timer = setInterval(() => setCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown, homeUrl]);

  const accent = isSuccess ? "#059669" : "#dc2626";
  const accentLight = isSuccess ? "#d1fae5" : "#fee2e2";
  const accentMid = isSuccess ? "#a7f3d0" : "#fca5a5";
  const bgGradient = isSuccess
    ? "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 40%, #f0f9ff 100%)"
    : "linear-gradient(135deg, #fff5f5 0%, #fef2f2 40%, #fff5f5 100%)";

  const icon = isSuccess ? (
    // Checkmark
    <svg width="44" height="44" fill="none" stroke="#059669" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M5 13l4 4L19 7" />
    </svg>
  ) : (
    // X mark
    <svg width="44" height="44" fill="none" stroke="#dc2626" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );

  const heading = isSuccess
    ? "Payment Successful!"
    : isAborted
    ? "Payment Cancelled"
    : "Payment Failed";

  const message = isSuccess
    ? "Your payment has been received successfully. You will receive a confirmation shortly."
    : isAborted
    ? "You cancelled the payment. No amount has been charged. Please try again when you are ready."
    : "Your payment could not be processed. Please try again or contact support if the issue persists.";

  return (
    <div style={{
      minHeight: "100vh",
      background: bgGradient,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      padding: "24px",
    }}>
      <div style={{
        background: "white",
        borderRadius: "20px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)",
        padding: "52px 48px",
        maxWidth: "480px",
        width: "100%",
        textAlign: "center",
        animation: "fadeInUp 0.5s ease",
      }}>
        {/* Status icon */}
        <div style={{
          width: "88px",
          height: "88px",
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accentLight}, ${accentMid})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 28px",
          animation: "popIn 0.5s cubic-bezier(0.34,1.56,0.64,1)",
        }}>
          {icon}
        </div>

        <h1 style={{
          fontSize: "28px",
          fontWeight: "800",
          color: "#111827",
          marginBottom: "12px",
          letterSpacing: "-0.5px",
        }}>
          {heading}
        </h1>

        <p style={{
          fontSize: "15px",
          color: "#6b7280",
          lineHeight: "1.7",
          marginBottom: "16px",
        }}>
          {message}
        </p>

        {orderId && (
          <p style={{
            fontSize: "12px",
            color: "#9ca3af",
            marginBottom: "8px",
            fontFamily: "monospace",
          }}>
            Order Ref: {orderId}
          </p>
        )}

        <p style={{
          fontSize: "13px",
          color: "#9ca3af",
          marginBottom: "28px",
          fontStyle: "italic",
        }}>
          Redirecting in <span style={{ color: accent, fontWeight: "600" }}>{countdown}</span> seconds...
        </p>

        <div style={{
          height: "1px",
          background: "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
          marginBottom: "28px",
        }} />

        <button
          onClick={() => { window.location.href = homeUrl; }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: `linear-gradient(135deg, ${accent}, ${isSuccess ? "#047857" : "#b91c1c"})`,
            color: "white",
            border: "none",
            borderRadius: "12px",
            padding: "14px 36px",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: `0 4px 14px ${isSuccess ? "rgba(5,150,105,0.35)" : "rgba(220,38,38,0.30)"}`,
            letterSpacing: "0.2px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Back to Home
        </button>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.5); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default PaymentResultPage;
